// DEPENDENCIES
import React from 'react';
import PropTypes from 'prop-types';
import 'flatpickr/dist/themes/material_green.css';

// COMPONENTS
import InputMask from 'react-input-mask';
import Flatpickr from 'react-flatpickr';
import { Link } from 'react-router-dom';

// ICONS
import { AiOutlineLoading } from 'react-icons/ai';
import { CgClose } from 'react-icons/cg';

// CUSTOM COMPONENTS
import CustomBlock from './CustomBlock';

const InputBlock = (props) => {
  const {
    type,
    className,
    style,
    autoFocus,
    disabled,
    iconLeft,
    iconRight,
    iconLeftOnClick,
    iconRightOnClick,
    clearAction,
    clearActionOnClick,
    inputRightButtonText,
    inputRightButtonBackgroundColor,
    inputRightButtonOnClick,
    inputLabel,
    label,
    inputState,
    containerClassName,
    backgroundColor,
    bordered,
    loading,
    isRequired,
    to,
    errorMessage,
    warningMessage,
    successMessage,
    ...inputProps
  } = props;

  const errorMessageSuffix = 'is required';

  const inputStates = {
    error: 'error',
    warning: 'warning',
    success: 'success',
    disabled: 'disabled',
  };

  const hasPrimaryRightAction = Boolean(loading || iconRight);
  const hasClearAction = Boolean(clearAction);
  const hasRightButton = Boolean(inputRightButtonText);
  const hasRightContent = hasPrimaryRightAction || hasClearAction || hasRightButton;

  const hasInteractiveRightContent = Boolean(iconRightOnClick || clearActionOnClick || inputRightButtonOnClick);

  const rightActionCount = Number(hasPrimaryRightAction) + Number(hasClearAction);

  const calculatedInputStyle = {
    paddingLeft: iconLeft ? 50 : 15,
    paddingRight: hasRightButton ? 160 : rightActionCount > 0 ? rightActionCount * 40 + 15 : 15,
  };

  const mergedInputStyle = {
    ...style,
    ...calculatedInputStyle,
  };

  const inputClassName = [
    'input-block',
    className,
    backgroundColor || 'light--sbg',
    inputState ? inputStates[inputState] : '',
    bordered ? 'bordered' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputContainerClassName = ['input-block-container', containerClassName].filter(Boolean).join(' ');

  const controlClassName = ['input-control-container', !inputLabel ? 'padded' : ''].filter(Boolean).join(' ');

  const rightContainerClassName = [
    'icon-right-container',
    hasRightButton ? 'has-button-text' : '',
    !hasInteractiveRightContent ? 'decorative' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isDisabled = disabled || inputState === inputStates.disabled;

  const stateMessageClassName = ['input-state-text', !inputLabel || label ? 'unpadded' : ''].filter(Boolean).join(' ');

  const fieldName = inputLabel || label || 'Input';
  const resolvedErrorMessage = errorMessage || `${fieldName} ${errorMessageSuffix}`;

  const renderLeftIcon = () => {
    if (!iconLeft) {
      return null;
    }

    if (iconLeftOnClick) {
      return (
        <CustomBlock className='icon-left-container interactive'>
          <button type='button' tabIndex={-1} onClick={iconLeftOnClick} aria-label='Input action'>
            {iconLeft}
          </button>
        </CustomBlock>
      );
    }

    return (
      <CustomBlock className='icon-left-container decorative'>
        <span aria-hidden='true'>{iconLeft}</span>
      </CustomBlock>
    );
  };

  const renderRightIcon = () => {
    if (!iconRight) {
      return null;
    }

    if (iconRightOnClick) {
      return (
        <button type='button' tabIndex={-1} onClick={iconRightOnClick} aria-label='Input action'>
          {iconRight}
        </button>
      );
    }

    return <span aria-hidden='true'>{iconRight}</span>;
  };

  const commonInputProps = {
    ...inputProps,
    className: inputClassName,
    type: type || 'text',
    autoFocus: Boolean(autoFocus),
    disabled: isDisabled,
    style: mergedInputStyle,
  };

  return (
    <CustomBlock className={inputContainerClassName}>
      {inputLabel && (
        <span className={`static-floating-label ${inputState || ''}`}>
          {inputLabel}

          {isRequired && <span className='danger--clr fw-600'>*</span>}
        </span>
      )}

      {label && (
        <span className={`static-label ${inputState || ''}`}>
          {label}

          {isRequired && <span className='danger--clr fw-600'>*</span>}
        </span>
      )}

      <CustomBlock className={controlClassName}>
        {renderLeftIcon()}

        {type === 'date' ? <Flatpickr {...commonInputProps} /> : <InputMask {...commonInputProps} />}

        {hasRightContent && (
          <CustomBlock className={rightContainerClassName}>
            {loading ? (
              <span className='loading-indicator-container' aria-hidden='true'>
                <AiOutlineLoading className='loading-indicator rotating success--clr' size={22} />
              </span>
            ) : (
              renderRightIcon()
            )}

            {clearAction && (
              <button type='button' tabIndex={-1} onClick={clearActionOnClick} aria-label='Clear input'>
                <CgClose className='mid-grey--clr' size={22} />
              </button>
            )}

            {inputRightButtonText &&
              (to ? (
                <Link
                  tabIndex={-1}
                  to={to}
                  onClick={inputRightButtonOnClick}
                  className={`input-right-button ${inputRightButtonBackgroundColor || 'light-grey--sbg'}`}
                >
                  {inputRightButtonText}
                </Link>
              ) : (
                <button
                  type='button'
                  tabIndex={-1}
                  onClick={inputRightButtonOnClick}
                  className={`input-right-button ${inputRightButtonBackgroundColor || 'light-grey--sbg'}`}
                >
                  {inputRightButtonText}
                </button>
              ))}
          </CustomBlock>
        )}
      </CustomBlock>

      {inputState === inputStates.error && <span className={`${stateMessageClassName} error-message`}>{resolvedErrorMessage}</span>}

      {inputState === inputStates.warning && <span className={`${stateMessageClassName} warning-message`}>{warningMessage}</span>}

      {inputState === inputStates.success && <span className={`${stateMessageClassName} success-message`}>{successMessage}</span>}
    </CustomBlock>
  );
};

InputBlock.propTypes = {
  // CONTAINER PROPS
  containerClassName: PropTypes.string,

  // LABEL PROPS
  label: PropTypes.string,
  inputLabel: PropTypes.string,

  // STATE PROPS
  inputState: PropTypes.oneOf(['error', 'warning', 'success', 'disabled']),
  errorMessage: PropTypes.string,
  successMessage: PropTypes.string,
  warningMessage: PropTypes.string,

  // INPUT PROPS
  type: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  backgroundColor: PropTypes.string,
  placeholder: PropTypes.string,

  // NAVIGATION PROPS
  to: PropTypes.string,

  // ITEMS AND ICON PROPS
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  inputRightButtonText: PropTypes.string,
  inputRightButtonBackgroundColor: PropTypes.string,

  // FUNCTION PROPS
  iconLeftOnClick: PropTypes.func,
  iconRightOnClick: PropTypes.func,
  clearActionOnClick: PropTypes.func,
  inputRightButtonOnClick: PropTypes.func,

  // BOOLEAN PROPS
  isRequired: PropTypes.bool,
  loading: PropTypes.bool,
  clearAction: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  bordered: PropTypes.bool,
};

export default InputBlock;
